import { useState, useEffect } from "react";
import { Mail, Send, Save, RefreshCw, CheckCircle2, XCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import { API, api } from "@/lib/api";
import { Card, Spin, NAVY, fmtDateTime } from "./bits";

const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200";
const labelCls = "block text-[11px] font-medium text-gray-500 mb-1";

const StatusPill = ({ send }) => {
  if (!send) return <span className="text-[11px] text-gray-400">Jamais envoyé</span>;
  const map = {
    success: { icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-600", txt: "Succès" },
    error: { icon: XCircle, cls: "bg-red-50 text-red-600", txt: "Échec" },
    pending: { icon: Clock, cls: "bg-amber-50 text-amber-600", txt: "En cours" },
  };
  const s = map[send.status] || map.pending;
  const I = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${s.cls}`}
      title={send.error || undefined}>
      <I size={11} /> {s.txt} · {send.trigger === "manual" ? "manuel" : "planifié"} · {fmtDateTime(send.at)}
    </span>
  );
};

export default function EmailSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [tenants, setTenants] = useState(null);
  const [history, setHistory] = useState([]);
  const [secrets, setSecrets] = useState({ resend_api_key: "", smtp_password: "" });
  const [saving, setSaving] = useState(false);
  const [sendingNow, setSendingNow] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.post(`${API}/admin/email/test`, { to: testTo.trim() });
      toast.success(`Email de test envoyé à ${r.data.recipients[0]} (${r.data.provider})`);
    } catch (e) { toast.error(e.response?.data?.detail || "Test d'envoi impossible"); }
    setTesting(false);
    load();
  };

  const load = async () => {
    try {
      const [s, t, h] = await Promise.all([
        api.get(`${API}/admin/email/settings`),
        api.get(`${API}/admin/email/tenants`),
        api.get(`${API}/admin/email/history`),
      ]);
      setSettings(s.data.settings);
      setTenants(t.data.tenants.map(r => ({ ...r, _recipientsText: (r.recipients || []).join(", ") })));
      setHistory(h.data.history || []);
    } catch { toast.error("Chargement de la configuration email impossible"); }
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const body = { ...settings };
      if (secrets.resend_api_key.trim()) body.resend_api_key = secrets.resend_api_key.trim();
      if (secrets.smtp_password.trim()) body.smtp_password = secrets.smtp_password.trim();
      const r = await api.put(`${API}/admin/email/settings`, body);
      setSettings(r.data.settings);
      setSecrets({ resend_api_key: "", smtp_password: "" });
      toast.success("Configuration email enregistrée (secrets masqués)");
    } catch (e) { toast.error(e.response?.data?.detail || "Enregistrement impossible"); }
    setSaving(false);
  };

  const saveTenant = async (row) => {
    const recipients = row._recipientsText.split(",").map(s => s.trim()).filter(Boolean);
    try {
      await api.put(`${API}/admin/email/tenants/${row.tenant}`, {
        recipients, send_hour: row.send_hour, send_minute: row.send_minute, daily_enabled: row.daily_enabled,
        weekly_enabled: row.weekly_enabled, weekly_hour: row.weekly_hour, weekly_minute: row.weekly_minute,
      });
      toast.success(`${row.name} : configuration email enregistrée`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Enregistrement impossible"); }
  };

  const sendNow = async (row) => {
    setSendingNow(row.tenant);
    try {
      const r = await api.post(`${API}/admin/email/tenants/${row.tenant}/send-now`);
      toast.success(`Envoyé à ${r.data.recipients.length} destinataire(s) — ${r.data.actions_count} action(s)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Envoi impossible"); }
    setSendingNow(null);
    load();
  };

  const sendNowWeekly = async (row) => {
    setSendingNow(`w-${row.tenant}`);
    try {
      const r = await api.post(`${API}/admin/email/tenants/${row.tenant}/send-now-weekly`);
      toast.success(`Récap hebdo envoyé à ${r.data.recipients.length} destinataire(s) (semaine du ${r.data.period_from})`);
    } catch (e) { toast.error(e.response?.data?.detail || "Envoi impossible"); }
    setSendingNow(null);
    load();
  };

  const updRow = (tenant, patch) =>
    setTenants(ts => ts.map(r => (r.tenant === tenant ? { ...r, ...patch } : r)));

  if (!settings || !tenants) return <Spin />;
  const isResend = settings.provider === "resend";

  return (
    <div className="p-6 lg:p-8 max-w-[1200px] mx-auto" data-testid="sa-email-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2" style={{ color: NAVY, fontFamily: "Outfit, sans-serif" }}>
            <Mail size={22} /> Emails automatiques — Quotidien & Récap hebdo
          </h1>
          <p className="text-sm text-gray-400 mt-0.5">Actions prioritaires chaque matin + récapitulatif flotte PDF chaque lundi (Europe/Zurich) — destinataires configurés par tenant</p>
        </div>
        <button onClick={load} className="flex items-center gap-2 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg px-3.5 py-2 text-sm text-gray-600 transition-colors" data-testid="email-refresh-btn">
          <RefreshCw size={14} /> Actualiser
        </button>
      </div>

      {/* ── Config globale du service ── */}
      <Card className="p-5 mb-6" data-testid="email-settings-form">
        <h2 className="text-sm font-semibold uppercase tracking-wide mb-4" style={{ color: NAVY }}>Service d'envoi (global)</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Provider</label>
            <select value={settings.provider} onChange={e => setSettings({ ...settings, provider: e.target.value })}
              className={inputCls} data-testid="email-provider-select">
              <option value="resend">Resend (API)</option>
              <option value="smtp">SMTP</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Adresse expéditrice</label>
            <input value={settings.from_email} onChange={e => setSettings({ ...settings, from_email: e.target.value })}
              placeholder="rapports@logitrak.ch" className={inputCls} data-testid="email-from-input" />
          </div>
          <div>
            <label className={labelCls}>Nom d'affichage</label>
            <input value={settings.from_name} onChange={e => setSettings({ ...settings, from_name: e.target.value })}
              className={inputCls} data-testid="email-fromname-input" />
          </div>
          <div>
            <label className={labelCls}>Reply-to (optionnel)</label>
            <input value={settings.reply_to} onChange={e => setSettings({ ...settings, reply_to: e.target.value })}
              placeholder="support@logitrak.ch" className={inputCls} data-testid="email-replyto-input" />
          </div>
          {isResend ? (
            <div className="md:col-span-2">
              <label className={labelCls}>
                Clé API Resend {settings.resend_api_key_set && <span className="text-emerald-600 font-semibold">— définie (masquée)</span>}
              </label>
              <input type="password" value={secrets.resend_api_key}
                onChange={e => setSecrets({ ...secrets, resend_api_key: e.target.value })}
                placeholder={settings.resend_api_key_set ? "•••••••••••• (laisser vide pour conserver)" : "re_..."}
                className={inputCls} autoComplete="new-password" data-testid="email-resend-key-input" />
            </div>
          ) : (
            <>
              <div>
                <label className={labelCls}>Hôte SMTP</label>
                <input value={settings.smtp_host} onChange={e => setSettings({ ...settings, smtp_host: e.target.value })}
                  placeholder="mail.infomaniak.com" className={inputCls} data-testid="email-smtp-host-input" />
              </div>
              <div>
                <label className={labelCls}>Port</label>
                <input type="number" value={settings.smtp_port || ""} onChange={e => setSettings({ ...settings, smtp_port: e.target.value ? parseInt(e.target.value, 10) : null })}
                  placeholder="587" className={inputCls} data-testid="email-smtp-port-input" />
              </div>
              <div>
                <label className={labelCls}>Utilisateur SMTP</label>
                <input value={settings.smtp_username} onChange={e => setSettings({ ...settings, smtp_username: e.target.value })}
                  className={inputCls} data-testid="email-smtp-user-input" />
              </div>
              <div>
                <label className={labelCls}>
                  Mot de passe SMTP {settings.smtp_password_set && <span className="text-emerald-600 font-semibold">— défini (masqué)</span>}
                </label>
                <input type="password" value={secrets.smtp_password}
                  onChange={e => setSecrets({ ...secrets, smtp_password: e.target.value })}
                  placeholder={settings.smtp_password_set ? "•••••••••••• (laisser vide pour conserver)" : ""}
                  className={inputCls} autoComplete="new-password" data-testid="email-smtp-password-input" />
              </div>
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  <input type="checkbox" checked={settings.smtp_tls} onChange={e => setSettings({ ...settings, smtp_tls: e.target.checked })}
                    data-testid="email-smtp-tls-checkbox" /> STARTTLS
                </label>
              </div>
            </>
          )}
        </div>
        <div className="flex items-center justify-between mt-4">
          <p className="text-[11px] text-gray-400">Les secrets sont chiffrés (Fernet) et ne sont jamais réaffichés après enregistrement.</p>
          <button onClick={saveSettings} disabled={saving}
            className="flex items-center gap-2 text-white rounded-lg px-4 py-2 text-sm font-medium transition-opacity disabled:opacity-50"
            style={{ background: NAVY }} data-testid="email-settings-save">
            <Save size={14} /> {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-gray-100">
          <input value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="votre@adresse.ch"
            className={`${inputCls} max-w-xs`} data-testid="email-test-to-input" />
          <button onClick={sendTest} disabled={testing || !testTo.trim()}
            className="flex items-center gap-1.5 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg px-3.5 py-2 text-sm font-medium text-gray-600 transition-colors disabled:opacity-50"
            data-testid="email-test-send-btn">
            <Send size={13} /> {testing ? "Envoi…" : "Tester l'envoi"}
          </button>
          <span className="text-[11px] text-gray-400">Envoie un email de test (avec PDF d'exemple joint) via la configuration enregistrée — résultat historisé ci-dessous.</span>
        </div>
      </Card>

      {/* ── Config par tenant ── */}
      <h2 className="text-sm font-semibold uppercase tracking-wide mb-3" style={{ color: NAVY }}>Destinataires & planification par tenant</h2>
      <div className="space-y-2 mb-8">
        {tenants.map(row => (
          <Card key={row.tenant} className="p-4" data-testid={`email-tenant-row-${row.tenant}`}>
            <div className="flex flex-col lg:flex-row lg:items-center gap-3">
              <div className="min-w-[180px]">
                <p className="text-sm font-semibold text-[#10265c]">{row.name}</p>
                <p className="text-[11px] text-gray-400">{row.tenant}{row.is_test ? " · TEST" : ""}{!row.is_active ? " · suspendu" : ""}</p>
              </div>
              <input value={row._recipientsText}
                onChange={e => updRow(row.tenant, { _recipientsText: e.target.value })}
                placeholder="gestionnaire@client.ch, direction@client.ch"
                className={`${inputCls} flex-1`} data-testid={`email-tenant-recipients-${row.tenant}`} />
              <input type="time"
                value={`${String(row.send_hour).padStart(2, "0")}:${String(row.send_minute).padStart(2, "0")}`}
                onChange={e => {
                  const [h, m] = e.target.value.split(":").map(Number);
                  updRow(row.tenant, { send_hour: h, send_minute: m });
                }}
                className="border border-gray-200 rounded-lg px-2 py-2 text-sm" data-testid={`email-tenant-time-${row.tenant}`} />
              <label className="flex items-center gap-1.5 text-xs text-gray-600 whitespace-nowrap">
                <input type="checkbox" checked={row.daily_enabled}
                  onChange={e => updRow(row.tenant, { daily_enabled: e.target.checked })}
                  data-testid={`email-tenant-enabled-${row.tenant}`} /> Quotidien
              </label>
              <button onClick={() => saveTenant(row)}
                className="flex items-center gap-1.5 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg px-3 py-2 text-xs font-medium text-gray-600 transition-colors"
                data-testid={`email-tenant-save-${row.tenant}`}>
                <Save size={12} /> Enregistrer
              </button>
              <button onClick={() => sendNow(row)} disabled={sendingNow === row.tenant}
                className="flex items-center gap-1.5 text-white rounded-lg px-3 py-2 text-xs font-medium transition-opacity disabled:opacity-50"
                style={{ background: NAVY }} data-testid={`email-send-now-${row.tenant}`}>
                <Send size={12} /> {sendingNow === row.tenant ? "Envoi…" : "Envoyer maintenant"}
              </button>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="text-[10px] font-semibold uppercase text-gray-400 min-w-[110px]">Quotidien</span>
              <StatusPill send={row.last_send} />
            </div>
            <div className="mt-2 pt-2 border-t border-gray-50 flex flex-wrap items-center gap-3">
              <span className="text-[10px] font-semibold uppercase text-gray-400 min-w-[110px]">Récap hebdo (lundi)</span>
              <input type="time"
                value={`${String(row.weekly_hour ?? 7).padStart(2, "0")}:${String(row.weekly_minute ?? 30).padStart(2, "0")}`}
                onChange={e => {
                  const [h, m] = e.target.value.split(":").map(Number);
                  updRow(row.tenant, { weekly_hour: h, weekly_minute: m });
                }}
                className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs" data-testid={`email-tenant-weekly-time-${row.tenant}`} />
              <label className="flex items-center gap-1.5 text-xs text-gray-600 whitespace-nowrap">
                <input type="checkbox" checked={!!row.weekly_enabled}
                  onChange={e => updRow(row.tenant, { weekly_enabled: e.target.checked })}
                  data-testid={`email-tenant-weekly-enabled-${row.tenant}`} /> Hebdo activé
              </label>
              <button onClick={() => sendNowWeekly(row)} disabled={sendingNow === `w-${row.tenant}`}
                className="flex items-center gap-1.5 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors disabled:opacity-50"
                data-testid={`email-send-weekly-now-${row.tenant}`}>
                <Send size={12} /> {sendingNow === `w-${row.tenant}` ? "Envoi…" : "Envoyer récap hebdo"}
              </button>
              <StatusPill send={row.last_send_weekly} />
            </div>
          </Card>
        ))}
      </div>

      {/* ── Historique ── */}
      <h2 className="text-sm font-semibold uppercase tracking-wide mb-3" style={{ color: NAVY }}>Historique des envois</h2>
      <Card className="overflow-x-auto" data-testid="email-history-table">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-gray-100">
              {["Date", "Tenant", "Type", "Déclencheur", "Statut", "Destinataires", "Détail"].map(hd => (
                <th key={hd} className="px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">{hd}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {history.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-sm text-gray-400">Aucun envoi pour le moment</td></tr>
            )}
            {history.map(h => (
              <tr key={h.id} className="border-b border-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600 whitespace-nowrap">{fmtDateTime(h.completed_at || h.created_at)}</td>
                <td className="px-4 py-2 text-xs font-medium text-[#10265c]">{h.tenant}</td>
                <td className="px-4 py-2 text-xs text-gray-500">
                  {h.kind === "weekly_report" ? "Récap hebdo" : h.kind === "test" ? "Test" : "Quotidien"}
                </td>
                <td className="px-4 py-2 text-xs text-gray-500">{h.trigger === "manual" ? "Manuel" : "Planifié"}</td>
                <td className="px-4 py-2">
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                    h.status === "success" ? "bg-emerald-50 text-emerald-600"
                      : h.status === "error" ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-600"}`}>
                    {h.status === "success" ? "Succès" : h.status === "error" ? "Échec" : "En cours"}
                  </span>
                </td>
                <td className="px-4 py-2 text-xs text-gray-500">{(h.recipients || []).length || "—"}</td>
                <td className="px-4 py-2 text-xs text-gray-400 max-w-[300px] truncate" title={h.error || h.subject}>
                  {h.error || h.subject || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
